// src/store/app
import merge from '@bundled-es-modules/deepmerge'
import { createSlice } from '@reduxjs/toolkit'
import { getProperty as getProp, setProperty as setProp } from 'dot-prop'
import initial from './initial'
import { promptLastIndex } from './selectors'
import { getPersistKey } from '../persistUserId'

const logger = console.scope('app-reducer', true)

const MAX_PROMPT_HISTORY = 50

export const initialState = initial

// Conform state, flatten objects tree

export const appSlice = createSlice({
	name: 'app',
	initialState,
	reducers: {
		// app
		setAblyState: (s, { payload }) => {
			s.ably_state = payload
		},
		setProjectId: (s, { payload }) => {
			if (typeof payload === 'object') {
				if (!payload.id) {
					logger.error('setProjectId: No id found on payload', payload)
					return
				}
				s.project_id = payload.id
				s.is_template = payload.is_template || false
				s.is_examples = payload.is_examples || null
			} else {
				s.project_id = payload
				s.is_template = false
				s.is_examples = null
			}
			if (!s.is_template && !s.is_examples) {
				localStorage.setItem(getPersistKey('project_id'), s.project_id)
			}
		},
		setNetworkQuality: (s, { payload }) => {
			s.network_quality[payload.direction] = payload
		},
		setSourceQuality: (s, { payload }) => {
			s.source_quality = payload
		},
		setSessionState: (s, { payload }) => {
			Object.assign(s.session_state, payload)
			if (payload.control_mode) {
				s.control_mode = payload.control_mode
			}
		},
		setControlMode: (s, { payload }) => {
			s.session_state.control_mode = payload
			s.control_mode = payload
		},
		setSessionProgress: (s, { payload }) => {
			Object.assign(s.session_loading, payload)
		},
		setRTCState: (s, { payload }) => {
			s.rtc_state = payload
		},
		setDCState: (s, { payload }) => {
			s.dc_state = payload
			if (payload !== 'open') {
				s.output_devices_received = false
			}
		},
		bumpPCGeneration: s => {
			s.pc_generation++
		},
		setRTCConnected: (s, { payload }) => {
			if (payload === undefined) {
				throw Error('setRTCConnected: payload is undefined')
			}
			s.rtc_state = payload ? 'connected' : 'disconnected'
		},
		setFPSStats: (s, { payload }) => {
			s.fps_stats = payload
		},
		setVideoQuality: (s, { payload }) => {
			s.video_quality = payload
			// Also update stats.video_quality for unified stats
			s.stats.video_quality = payload
		},
		setStats: (s, { payload }) => {
			s.stats = { ...s.stats, ...payload }
		},
		setPresence: (s, { payload }) => {
			s.presence = payload
		},
		setSessionPresence: (s, { payload }) => {
			s.session_presence = payload
		},
		setClientMode: (s, { payload }) => {
			s.client_mode = payload
		},
		setActiveDevice: (s, { payload }) => {
			s.active_device = payload
			s.source_device = payload
		},
		setSourceDevice: (s, { payload }) => {
			s.source_device = payload
			s.active_device = payload
		},
		/** A presence naming the runner; kept across a DC drop, so a reconnect gap never hands the role to a second desktop. */
		setRunnerDevice: (s, { payload }) => {
			s.runner_device = payload
			s.runner_supported = true
		},
		setDCConnections: (s, { payload }) => {
			s.dc_connections = payload
		},
		setOutputDevices: (s, { payload }) => {
			s.output_devices = payload
			s.output_devices_received = true
		},
		setPendingOutputs: (s, { payload }) => {
			s.pending_outputs = payload
		},
		setAdvancedControls: (s, { payload }) => {
			s.advanced_controls = payload
		},
		setAppProperty: (s, { payload }) => {
			const [k, v] = payload
			setProp(s, k, v)
		},
		patchAppProperty: (s, { payload }) => {
			const [k, v] = payload
			const value = getProp(s, k)
			setProp(s, k, merge(value, v))
		},
		setRequestedControlMode: (s, { payload }) => {
			s.control_mode = payload
		},
		toggleShowStats: s => {
			s.show_stats = !s.show_stats
		},
		/**
		 * Record a prompt that became live. Dispatched ONLY by `promptHistory` middleware, which watches `live.diffusion.prompt` — a user edit, a preset load and a peer's change all arrive here identically, so no caller decides what counts as history.
		 *
		 * ⚠️ Most-recently-used order: a re-recorded prompt moves to the end. Revisiting a preset must leave "the one before this" reachable, which first-use order does not.
		 */
		recordPrompt: (s, { payload }) => {
			const p = s.prompt
			// Indices no longer describe the array once it reorders.
			p.browse = null
			const at = p.history.indexOf(payload)
			if (at >= 0) p.history.splice(at, 1)
			p.history.push(payload)
			if (p.history.length > MAX_PROMPT_HISTORY) p.history.shift()
		},
		/**
		 * Step the prev/next cursor. Records nothing, applies nothing — the field shows the value, and only a commit reaches `live`.
		 *
		 * The cursor indexes history and nothing else; `draft` is the text browsing started from, restored on stepping forward past the newest entry. A draft is state to return to, never a position to land on — as a slot it duplicates the newest entry whenever the field already shows it, and prev/next then toggle between two identical strings.
		 */
		stepPrompt: (s, { payload }) => {
			const p = s.prompt
			const { length } = p.history
			if (!length) return
			const { delta, text: given } = payload
			const text = given ?? ''
			// Typing over a browsed entry ends the session, so the typing becomes the new draft instead of being discarded.
			if (p.browse && given !== undefined) {
				const { index, draft } = p.browse
				if (given !== (index < length ? p.history[index] : draft)) p.browse = null
			}
			// Not browsing: start from the entry the field is showing, else past the end.
			const at = p.browse?.index ?? p.history.indexOf(text)
			const from = at >= 0 ? at : length
			const draft = p.browse?.draft ?? text
			const index = from + delta
			// Same bound the buttons read, so the reducer cannot step where they say it cannot.
			if (index < 0 || index > promptLastIndex(p.history, draft)) return
			// `index === length` is past the newest — not an entry, but still browsing: it restores the draft, and stepping back must return to history rather than re-reading the field.
			p.browse = { index, draft }
		},
		clearPromptBrowse: s => {
			s.prompt.browse = null
		},
	},
})

export default appSlice.reducer

export const {
	setProjectId,
	setAblyState,
	setRTCState,
	setDCState,
	bumpPCGeneration,
	setRTCConnected,
	setFPSStats,
	setVideoQuality,
	setStats,
	setPresence,
	setSessionPresence,
	setClientMode,
	setActiveDevice,
	setSourceDevice,
	setRunnerDevice,
	setDCConnections,
	setOutputDevices,
	setPendingOutputs,
	setAdvancedControls,
	setSessionState,
	setNetworkQuality,
	setSourceQuality,
	setControlMode,
	setSessionProgress,
	setAppProperty,
	patchAppProperty,
	recordPrompt,
	stepPrompt,
	clearPromptBrowse,
	setRequestedControlMode,
	toggleShowStats,
} = appSlice.actions
